// +----------------------------------------------------------------------
// | Copyright (c) 2020~2023 http://www.vuecmf.com All rights reserved.
// +----------------------------------------------------------------------
// | Licensed ( https://github.com/emei8/vuecmf/blob/master/LICENSE )
// +----------------------------------------------------------------------
// | Author: emei8 <2278667823@qq.com>
// +----------------------------------------------------------------------

import {onMounted, Prop, reactive, Ref, ref, toRefs, ToRefs} from "vue";
import EventService from './EventService'
import {VuecmfEditor} from "./typings/VuecmfEditor";
import AnyObject = VuecmfEditor.AnyObject;
import {ElMessage, ElMessageBox} from "element-plus";
import AMapLoader from "@amap/amap-jsapi-loader";



/**
 * vuecmf editor 服务类
 */
export default class Service {
    editor_ref = ref();                           //编辑器ref
    content = ref();                              //组件传入的编辑器内容
    upload_url = ref('');                   //组件传入的文件上传地址
    token = ref('');                        //上传header中传入的token
    params = ref();                       //上传文件时传入的额外参数

    link_dlg = ref(false);                  //是否显示超链接对话框
    link_url = ref('');                     //超链接地址
    link_target = ref('_blank');            //超链接打开方式

    about_dlg = ref(false);                 //是否显示关于对话框
    color_value = ref('#000000');           //字体颜色值
    bg_color_value = ref('#ffffff');        //文本背景色
    predefine_colors = ref([
        '#000000',
        '#ff4500',
        '#ff8c00',
        '#ffd700',
        '#90ee90',
        '#00ced1',
        '#1e90ff',
        '#c71585',
        'rgba(255, 69, 0, 0.68)',
        'rgb(255, 120, 0)',
        'hsv(51, 100, 98)',
        'hsva(120, 40, 94, 0.5)',
        'hsl(181, 100%, 37%)',
        'hsla(209, 100%, 56%, 0.73)',
        '#c7158577',
    ]);                //预设颜色值

    table_dlg = ref(false);                 //是否显示插入表格对话框
    table_row = ref(1);                     //插入表格的行数
    table_col = ref(1);                     //插入表格的列数
    table_width = ref(300);                 //插入表格的宽度
    table_border_color = ref('#DCDFE6');    //插入的表格边框颜色
    table_head = ref('first_row');          //第一行为列头
    table_head_bg_color = ref('#EBEEF5');   //列头背景色
    table_head_color = ref('#666666');      //列头字体颜色

    //文件配置信息
    file_config = reactive({
        image: ref<AnyObject>(),  //图片
        file: ref<AnyObject>(),   //文件
        video: ref<AnyObject>(),  //视频
    })

    image_dlg = ref(false);                 //是否显示插入图片对话框
    video_dlg = ref(false);                 //是否显示插入视频对话框
    file_dlg = ref(false);                  //是否显示插入文件对话框

    image_form = ref();                            //图片上传表单
    video_form = ref();                            //视频上传表单
    file_form = ref();                             //文件上传表单

    code_dlg = ref(false);                  //是否显示插入代码对话框
    code_content = ref('');                 //插入代码内容

    ai_dlg = ref(false);   //是否打开ai生成内容弹窗
    ai_question = ref('');  //向openai提的问题
    ai_content = ref('');   //openai返回的内容
    openai_url = ref('');   //openai请求API

    amapObj = ref();
    amapInstance = ref(); //地图实例
    map_dlg = ref(false);   //是否打开map弹窗
    map_container = ref('container'); //地图容器ID
    //当前地图信息
    map_info = reactive({
        name: '深圳市政府',
        contact: '电话：0755-12345\n地址：广东省深圳市福田区福中三路市民中心C区',
        width: '100%',
        height: '300'
    })
    map_center = reactive({lng: 114.057956, lat: 22.544003}); //当前地图坐标
    amap_key = ref(''); //amap app key

    editor_tool_config: AnyObject;                //编辑器工具配置
    eventService: EventService;                   //事件服务类实例

    /**
     * 构造器
     * @param id        编辑器ID标识
     * @param content   编辑器内容
     * @param emit      emit对象
     * @param tools     自定义工具条，为空 则显示所有功能
     * @param upload    上传文件的后端地址
     */
    constructor(id: Ref<[StringConstructor] extends [Prop<infer V, infer D>] ? (unknown extends infer V ? IfAny<V, V, D> : V) : (StringConstructor | undefined)> | undefined,
                content: Ref<[StringConstructor] extends [Prop<infer V, infer D>] ? (unknown extends infer V ? IfAny<V, V, D> : V) : (StringConstructor | undefined)> | undefined,
                emit: EmitFn<EE[]>,
                tools?: string | undefined,
                upload?: Ref<[StringConstructor] extends [Prop<infer V, infer D>] ? (unknown extends infer V ? IfAny<V, V, D> : V) : (StringConstructor | undefined)> | undefined,
                token?: Ref<[StringConstructor] extends [Prop<infer V, infer D>] ? (unknown extends infer V ? IfAny<V, V, D> : V) : (StringConstructor | undefined)> | undefined,
                params?: Ref<[StringConstructor|ObjectConstructor] extends [Prop<infer V, infer D>] ? (unknown extends infer V ? IfAny<V, V, D> : V) : (StringConstructor | ObjectConstructor | undefined)> | undefined,
                openai?: Ref<[StringConstructor] extends [Prop<infer V, infer D>] ? (unknown extends infer V ? IfAny<V, V, D> : V) : (StringConstructor | undefined)> | undefined,
                amap_key?: Ref<[StringConstructor] extends [Prop<infer V, infer D>] ? (unknown extends infer V ? IfAny<V, V, D> : V) : (StringConstructor | undefined)> | undefined
    ) {
        console.log('vuecmf-editor service init')
        this.content = content
        this.eventService = new EventService(id, this.editor_ref, emit)
        this.link_dlg.value = false
        this.upload_url.value = upload
        this.token.value = token
        this.params.value = params
        this.openai_url.value = openai
        this.amap_key.value = amap_key

        //初始化文件信息
        this.file_config.image = {
            url: '',          //图片地址
            image_width: 300,       //图片宽度
            image_height: 200,      //图片高度
            image_align: 'middle',  //图片对齐方式
        }
        this.file_config.file = {
            url: '',           //文件链接地址
            file_target: '_blank',  //文件链接打开方式
        }
        this.file_config.video = {
            url: '',          //视频地址
            video_width: 300,       //视频宽度
            video_height: 200,      //视频高度
            video_align: 'center',  //视频对齐方式
        }

        this.editor_tool_config = [
            {name:'heading', icon: 'bi-type-h1', tips: '标题', fun: this.eventService.headingEvent, list: [
                    {key:'H1', value: '<h1>一级标题</h1>'},
                    {key:'H2', value: '<h2>二级标题</h2>'},
                    {key:'H3', value: '<h3>三级标题</h3>'},
                    {key:'H4', value: '<h4>四级标题</h4>'},
                    {key:'H5', value: '<h5>五级标题</h5>'},
                    {key:'H6', value: '<h6>六级标题</h6>'},
                ]},
            {name:'align-left',icon: 'bi-text-left', tips: '左对齐', fun: this.eventService.leftAlignEvent},
            {name:'align-center',icon: 'bi-text-center', tips: '居中对齐', fun: this.eventService.centerAlignEvent},
            {name:'align-right',icon: 'bi-text-right', tips: '右对齐', fun: this.eventService.rightAlignEvent},
            {name:'bold',icon: 'bi-type-bold', tips: '加粗', fun: this.eventService.boldEvent},
            {name:'italic',icon: 'bi-type-italic', tips: '斜体', fun: this.eventService.italicEvent},
            {name:'underline',icon: 'bi-type-underline', tips: '下横线', fun: this.eventService.underlineEvent},
            {name:'strikethrough',icon: 'bi-type-strikethrough', tips: '删除线', fun: this.eventService.throughEvent},
            {name:'superscript',icon: 'bi-superscript', tips: '上角标', fun: this.eventService.supFontEvent},
            {name:'subscript',icon: 'bi-subscript', tips: '下角标', fun: this.eventService.subFontEvent},
            {name:'quote-left',icon: 'bi-blockquote-left', tips: '块引用', fun: this.eventService.blockquoteEvent},
            {name:'link',icon: 'bi-lightbulb', tips: '添加超链接', fun: this.eventService.linkEvent},
            {name:'unlink',icon: 'bi-lightbulb-off', tips: '取消超链接', fun: this.eventService.unlinkEvent},
            {name:'font',icon: 'bi-fonts', tips: '字体', fun: this.eventService.fontNameEvent, list:[
                    {key:'Arial', value: '<span style="font-family: Arial">Arial</span>'},
                    {key:'Helvetica', value: '<span style="font-family: Helvetica">Helvetica</span>'},
                    {key:'Tahoma', value: '<span style="font-family: Tahoma">Tahoma</span>'},
                    {key:'Verdana', value: '<span style="font-family: Verdana">Verdana</span>'},
                    {key:'Lucida Grande', value: '<span style="font-family: Lucida Grande">Lucida Grande</span>'},
                    {key:'Times New Roman', value: '<span style="font-family: Times New Roman">Times New Roman</span>'},
                    {key:'Georgia', value: '<span style="font-family: Georgia">Georgia</span>'},
                    {key:'SimSun', value: '<span style="font-family: SimSun">宋体</span>'},
                    {key:'SimHei', value: '<span style="font-family: SimHei">黑体</span>'},
                    {key:'Microsoft Yahei', value: '<span style="font-family: Microsoft Yahei">微软雅黑</span>'},
                    {key:'KaiTi', value: '<span style="font-family: KaiTi">楷体</span>'},
                    {key:'NSimSun', value: '<span style="font-family: NSimSun">新宋体</span>'},
                    {key:'FangSong', value: '<span style="font-family: FangSong">仿宋</span>'},
                ]},
            {name:'font-size',icon: 'bi-sort-down-alt', tips: '字大小', fun: this.eventService.fontSizeEvent, list:[
                    {key:'1', value: '<font size="1">1号字</font>'},
                    {key:'2', value: '<font size="2">2号字</font>'},
                    {key:'3', value: '<font size="3">3号字</font>'},
                    {key:'4', value: '<font size="4">4号字</font>'},
                    {key:'5', value: '<font size="5">5号字</font>'},
                    {key:'6', value: '<font size="6">6号字</font>'},
                    {key:'7', value: '<font size="7">7号字</font>'},
                ]},
            {name:'font-color',icon: 'glass-martini-alt', tips: '字体颜色', fun: this.eventService.foreColorEvent},
            {name:'font-bg-color',icon: 'glass-martini', tips: '文本背景色', fun: this.eventService.hiliteColorEvent},
            {name:'indent',icon: 'bi-text-indent-left', tips: '增加缩进', fun: this.eventService.indentEvent},
            {name:'outdent',icon: 'bi-text-indent-right', tips: '减少缩进', fun: this.eventService.outdentEvent},
            {name:'list-ol',icon: 'bi-list-ol', tips: '有序列表', fun: this.eventService.olEvent},
            {name:'list-ul',icon: 'bi-list-ul', tips: '无序列表', fun: this.eventService.ulEvent},
            {name:'select-all',icon: 'bi-check2-square', tips: '全选', fun: this.eventService.selectAllEvent},
            {name:'remove-format',icon: 'bi-slash-circle', tips: '清除格式', fun: this.eventService.removeFormatEvent},
            {name:'cut',icon: 'bi-scissors', tips: '剪切', fun: this.eventService.cutEvent},
            {name:'copy',icon: 'bi-files', tips: '复制', fun: this.eventService.copyEvent},
            {name:'paste',icon: 'bi-clipboard2', tips: '粘贴', fun: this.eventService.pasteEvent},
            {name:'hr',icon: 'bi-hr', tips: '插入水平线', fun: this.eventService.horizontalEvent},
            {name:'table',icon: 'bi-table', tips: '插入表格', fun: this.eventService.insertTableEvent},
            {name:'image',icon: 'bi-card-image', tips: '插入图片', fun: this.eventService.insertImageEvent},
            {name:'film',icon: 'bi-film', tips: '插入视频', fun: this.eventService.insertVideoEvent},
            {name:'file',icon: 'bi-file-earmark-text', tips: '插入文件', fun: this.eventService.insertFileEvent},
            {name:'code',icon: 'bi-code-slash', tips: '插入代码', fun: this.eventService.insertCodeEvent},
            {name:'redo',icon: 'bi-arrow-clockwise', tips: '恢复', fun: this.eventService.redoEvent},
            {name:'undo',icon: 'bi-arrow-counterclockwise', tips: '撤销', fun: this.eventService.undoEvent},
            {name:'clear',icon: 'bi-trash', tips: '清空所有内容', fun: this.clearEvent},
            {name:'openai',icon: 'bi-filetype-ai', tips: 'AI生成内容'},
            {name:'amap-map',icon: 'bi-pin-map', tips: '高德地图'},
            {name:'about',icon: 'bi-question-circle', tips: '关于'},
        ]

        //自定义工具条
        if(typeof tools != 'undefined' && tools.trim() != ''){
            const tool_cfg: VuecmfEditor.AnyObject = []
            const tool_arr = tools.trim().split(',')
            tool_arr.forEach((item: string, key: number) => {
                tool_arr[key] = item.trim()
            })
            this.editor_tool_config.forEach((item: AnyObject, index: number) => {
                if(tool_arr.indexOf(item.name) !== -1 || index == this.editor_tool_config.length - 1){
                    tool_cfg.push(item)
                }
            })
            this.editor_tool_config = tool_cfg
        }

    }


    /**
     * 编辑器初始化
     */
    init = ():void => {
        onMounted(()=>{
            this.editor_ref.value.innerHTML = this.content.value
            this.eventService.init()
        })
    }

    /**
     * 获取配置参数并导出
     */
    getFileConfig = (): ToRefs => {
        return toRefs(this.file_config)
    }

    /**
     * 设置超链接
     */
    linkEvent = ():void => {
        this.eventService.linkEvent(this.link_url, this.link_target)
        this.link_dlg.value = false
    }

    /**
     * 插入表格
     */
    insertTableEvent = ():void => {
        this.eventService.insertTableEvent(this.table_width.value, this.table_row.value, this.table_col.value, this.table_border_color.value, this.table_head.value, this.table_head_bg_color.value, this.table_head_color.value)
        this.table_dlg.value = false
    }

    /**
     * 插入图片
     */
    insertImageEvent = ():void => {
        if(this.file_config.image != null){
            this.eventService.insertImageEvent(
                this.file_config.image.url.trim(),
                this.file_config.image.image_width,
                this.file_config.image.image_height,
                this.file_config.image.image_align)
            this.image_dlg.value = false
        }
    }

    /**
     * 插入视频
     */
    insertVideoEvent = ():void => {
        if(this.file_config.video != null){
            this.eventService.insertVideoEvent(
                this.file_config.video.url.trim(),
                this.file_config.video.video_width,
                this.file_config.video.video_height,
                this.file_config.video.video_align)
            this.video_dlg.value = false
        }

    }

    /**
     * 插入文件
     */
    insertFileEvent = ():void => {
        if(this.file_config.file != null){
            this.eventService.insertFileEvent(this.file_config.file.url, this.file_config.file.file_target)
            this.file_dlg.value = false
        }

    }

    /**
     * 插入代码
     */
    insertCodeEvent = ():void => {
        this.eventService.insertCodeEvent(this.code_content.value.trim())
        this.code_dlg.value = false
    }

    /**
     * 触发文件上传
     */
    triggerUpload = (refForm: HTMLFormElement):void => {
        refForm.click()
    }

    /**
     * 上传文件
     * @param fileForm  文件表单对象
     * @param fileType  文件类型
     */
    uploadFile = (fileForm:AnyObject, fileType:string):void => {
        if(fileForm == null || fileForm.target == undefined || fileForm.target.files == undefined) return
        const fileObj = fileForm.target.files[0]
        const formData = new FormData()
        formData.append("file", fileObj)  //file表单的内容
        if(this.params.value != ''){
            let params_list:AnyObject = {}
            if(typeof this.params.value === 'string'){
                 params_list = JSON.parse(this.params.value)
            }else{
                 params_list = this.params.value
            }

            Object.keys(params_list).forEach((key) => {
                formData.append(key, params_list[key])
            })
        }

        fetch(this.upload_url.value, { method: 'POST', body: formData, headers:{ 'token': this.token.value } }).then(res => res.json()).then((res) => {
            switch (fileType){
                case 'file':
                    if(this.file_config.file != null) this.file_config.file['url'] = res.data.url
                    break;
                case 'video':
                    if(this.file_config.video != null) this.file_config.video['url'] = res.data.url
                    break;
                case 'image':
                    if(this.file_config.image != null) this.file_config.image['url'] = res.data.url
                    break;
            }

        }).catch((err) => {
            console.error(err)
        })
    }

    /**
     * 清空所有内容
     */
    clearEvent = ():void => {
        ElMessageBox.confirm(
            '确定要清空所有内容?',
            '提示',
            {
                confirmButtonText: '确定',
                cancelButtonText: '取消',
                type: 'warning',
            }
        ).then(() => {
            this.editor_ref.value.innerHTML = ''
        })
    }


    /**
     * AI生成内容
     */
    openaiEvent = () => {
        this.eventService.openaiEvent(this.ai_content.value.trim())
        this.ai_dlg.value = false
    }

    /**
     * AI生成内容调用后端事件
     */
    aiGc = () => {
        if(this.ai_question.value.trim() == ''){
            ElMessage.error('需求不能为空！')
            return false
        }

        const formData = new FormData()
        formData.append("question", this.ai_question.value)

        fetch(this.openai_url.value, { method: 'POST', body: formData, headers:{ 'token': this.token.value } }).then(res => res.json()).then((res) => {
            if(res.code == 0){
                this.ai_content.value = res.data
            }else{
                ElMessage.error(res.msg)
            }
        }).catch((err) => {
            console.error(err)
            ElMessage.error(err)
        })
    }

    /**
     * 打开地图窗口
     */
    openAMapDlg = () => {
        if(this.amap_key.value == '' || this.amap_key.value == '高德地图申请的应用KEY' || this.amap_key.value.length != 32){
            ElMessage.error('请先配置高德地图应用KEY')
            return
        }

        this.map_container.value = 'container_' + Math.round(Math.random()*100000000)
        this.map_dlg.value = true

        AMapLoader.load({
            key: this.amap_key.value,//首次load必填
            version: '2.0',
        }).then((AMap) => {
            const map = new AMap.Map(this.map_container.value,{
                resizeEnable: true,
                center: [this.map_center.lng, this.map_center.lat],
                zoom: 14
            });

            this.amapObj.value = AMap
            this.amapInstance.value = map

            //添加标注
            const addMarker = () => {
                map.clearMap();
                const marker = new AMap.Marker({
                    map: map,
                    position: map.getCenter(),
                    draggable: true
                });

                marker.on('dragging', (e:AnyObject) => {
                    const pos = e.target._position
                    this.map_center.lng = pos[0]
                    this.map_center.lat = pos[1]
                    map.setCenter(e.target._position)
                    this.openMapInfoWin()
                });
            }

            addMarker()
            this.openMapInfoWin()

        }).catch((e) => {
            console.error('err = ',e);
            ElMessage.error(e)
        });

    }

    /**
     * 打开地图信息窗口
     */
    openMapInfoWin = () => {
        const content = [
            '<div class="custom-info input-card content-window-card"><div><b>'+ this.map_info.name +'</b><br>',
            '<p>'+ this.map_info.contact.replace(/\n/g,'<br>') + '</p>',
            '</div><div class="info-bottom" style="position: relative; top: 0px; margin: 0px auto;"><img src="https://webapi.amap.com/images/sharp.png"></div></div>'
        ];

        // 创建 infoWindow 实例
        const infoWindow = new this.amapObj.value.InfoWindow({
            isCustom: true,  //使用自定义窗体
            content: content.join(""),  //传入 dom 对象，或者 html 字符串
            offset: new this.amapObj.value.Pixel(18, -42)
        });

        infoWindow.open(this.amapInstance.value, this.amapInstance.value.getCenter());
    }


    /**
     * 插入百度地图事件
     */
    insertMapEvent = () => {
        this.eventService.insertMapEvent(this.map_info, this.map_center,this.amap_key.value)
        this.map_dlg.value = false
    }

}
